/**
 * The floating chat surface chooser shared by the canvas and home/workspace
 * panels — one component that renders whichever of the four mutually-exclusive
 * surfaces the state selects:
 *
 *  - **Left dock** (`isOpen && dockLeft`, full-height): a resizable column on
 *    the LEFT of a full-width-expanded detail pane; the pane shrinks beside it
 *    (see `FloatingChatLeftDock`).
 *  - **Left window** (`isOpen && dockLeft`, windowed): floats bottom-left OVER
 *    the full-width pane (see `FloatingChatLeftWindow`).
 *  - **Corner window / right dock** (`isOpen`): the normal surface — a
 *    resizable floating window in the region's bottom-right corner, or docked
 *    flush-right at full height.
 *  - **FAB launcher** (closed): pinned bottom-right, or bottom-LEFT over a
 *    full-width pane (the `dockStyle` handed to `renderFab`).
 *
 * The panels own everything upstream of the choice — gating, geometry inputs,
 * and the clearance/dock-reserve side effects — and pass the pane content and
 * FAB as render props. Geometry quirks that differ per surface (the canvas's
 * container-query viewport escape, the workspace's tighter docked width cap)
 * come in as props.
 */

import { cn } from "@/utils";
import { ResizablePanel } from "@cohesive/ui-components";
import { AnimatePresence, motion } from "motion/react";
import type { CSSProperties, ReactNode, Ref } from "react";
import { FloatingChatLeftDock } from "./FloatingChatLeftDock";
import { FloatingChatLeftWindow } from "./FloatingChatLeftWindow";
import {
  CORNER_SHELL_BASE_CLASS,
  DOCKED_PANEL_CLASS,
  DOCKED_SHELL_BASE_CLASS,
  FLOATING_CHAT_INITIAL_WIDTH_PX,
  FLOATING_CHAT_MIN_WIDTH_PX,
  FLOATING_CHAT_MOTION_INITIAL,
  FLOATING_CHAT_MOTION_TRANSITION,
  FLOATING_WINDOW_PANEL_CLASS,
  SHELL_CONTAINER_FIXED_ESCAPE_CLASS,
} from "./floatingChatShell";
import type { FloatingChatSizeClass } from "./useFloatingChatSessionStack";

export interface FloatingChatSurfaceProps {
  /** The chat window is up (any mode); false renders the FAB launcher. */
  isOpen: boolean;
  /**
   * A full-width detail pane occupies the right, so open surfaces relocate to
   * the LEFT of it (and the FAB pins bottom-left). Ignored without `anchorRect`
   * — the fixed left modes are computed from the content-region rect.
   */
  dockLeft: boolean;
  /** windowed ↔ full-height; also the ResizablePanel remount key (see below). */
  sizeClass: FloatingChatSizeClass;
  /** Content-region rect the fixed (left/FAB-dock) modes anchor to. */
  anchorRect: DOMRect | null;
  viewportHeightPx: number;
  /** Per-surface ResizablePanel width-persistence key (all modes share it). */
  storageKey: string;
  /** Ref to the corner/right-dock motion shell (clearance + dock-reserve observers). */
  shellRef?: Ref<HTMLDivElement>;
  /**
   * Add the below-400px container-query escape to the corner/dock shell
   * (canvas: the React Flow wrapper is a CSS `@container`; the workspace
   * region isn't one, so it omits this).
   */
  escapeToViewportWhenNarrow?: boolean;
  /** Width cap for the windowed corner panel. */
  windowMaxWidthPx: number;
  /**
   * Width cap for the full-height right dock, when tighter than
   * `windowMaxWidthPx` (the workspace reserves a usable content column beside
   * the dock; the canvas caps both modes the same).
   */
  dockedMaxWidthPx?: number;
  /** Height bounds for the windowed modes (full-height fills the region). */
  initialHeightPx: number;
  minHeightPx: number;
  maxHeightPx: number;
  /** Width cap for the left-window mode (viewport-based — the region is ~0 wide). */
  leftWindowMaxWidthPx: number;
  /** The pane content; `isChatExpanded` mirrors the mode the pane renders in. */
  renderPane: (opts: { isChatExpanded: boolean }) => ReactNode;
  /** The FAB launcher; `dockStyle` is set when it should pin bottom-left. */
  renderFab: (dockStyle?: CSSProperties) => ReactNode;
  /** A width drag on the shell started / ended — surrounding chrome that eases
   *  into the shell's geometry follows the edge instead while it runs. */
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
}

export function FloatingChatSurface({
  isOpen,
  dockLeft,
  sizeClass,
  anchorRect,
  viewportHeightPx,
  storageKey,
  shellRef,
  escapeToViewportWhenNarrow,
  windowMaxWidthPx,
  dockedMaxWidthPx,
  initialHeightPx,
  minHeightPx,
  maxHeightPx,
  leftWindowMaxWidthPx,
  renderPane,
  renderFab,
  onResizeStart,
  onResizeEnd,
}: FloatingChatSurfaceProps) {
  const isFullHeight = sizeClass === "full-height";

  // ── Left side: the detail pane is expanded full-width ─────────────────────
  // The chat moves left of the detail. Geometry is fixed (computed from the
  // content-region rect) because the in-flow region is squeezed to ~0 width.
  if (isOpen && dockLeft && anchorRect) {
    if (isFullHeight) {
      // Docked resizable column; the detail shrinks beside it (the dock
      // reports its live width as `leftChatDockPx`, freeing exactly this strip).
      return (
        <FloatingChatLeftDock anchorRect={anchorRect} storageKey={storageKey}>
          {renderPane({ isChatExpanded: true })}
        </FloatingChatLeftDock>
      );
    }
    // Windowed: float in the bottom-left over the full-width detail.
    return (
      <FloatingChatLeftWindow
        anchorRect={anchorRect}
        viewportHeightPx={viewportHeightPx}
        storageKey={storageKey}
        maxWidthPx={leftWindowMaxWidthPx}
        initialHeightPx={initialHeightPx}
        minHeightPx={minHeightPx}
        maxHeightPx={maxHeightPx}
      >
        {renderPane({ isChatExpanded: false })}
      </FloatingChatLeftWindow>
    );
  }

  if (isOpen) {
    return (
      <AnimatePresence initial={false}>
        <motion.div
          key="window"
          ref={shellRef}
          data-floating-chat="window"
          // Windowed floats in the bottom-right corner; full-height docks flush
          // to the right edge and fills the shell height (definite-height flex).
          className={cn(
            isFullHeight ? DOCKED_SHELL_BASE_CLASS : CORNER_SHELL_BASE_CLASS,
            escapeToViewportWhenNarrow && SHELL_CONTAINER_FIXED_ESCAPE_CLASS,
          )}
          initial={FLOATING_CHAT_MOTION_INITIAL}
          animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
          exit={FLOATING_CHAT_MOTION_INITIAL}
          transition={FLOATING_CHAT_MOTION_TRANSITION}
        >
          <ResizablePanel
            // Remount across size classes: ResizablePanel manages height only
            // when a vertical handle exists, and leaves a stale inline height
            // behind when the handle set changes — a fresh instance avoids that.
            // `transitionDuration={0}` keeps the swap instant ("snap").
            key={sizeClass}
            // Full-height: width-only resize (left edge), with the drag pill
            // shown (like the RightPane). Windowed stays resizable on its
            // left/top edges + top-left corner but hides the pills so the
            // floating window reads clean; `directions` also drives its height
            // management.
            directions={isFullHeight ? ["left"] : ["left", "top", "top-left"]}
            isClosed={false}
            transitionDuration={0}
            minWidth={FLOATING_CHAT_MIN_WIDTH_PX}
            maxWidth={
              isFullHeight
                ? (dockedMaxWidthPx ?? windowMaxWidthPx)
                : windowMaxWidthPx
            }
            initialWidth={FLOATING_CHAT_INITIAL_WIDTH_PX}
            initialHeight={isFullHeight ? undefined : initialHeightPx}
            minHeight={isFullHeight ? undefined : minHeightPx}
            maxHeight={isFullHeight ? undefined : maxHeightPx}
            storageKey={storageKey}
            showHandleIndicator={isFullHeight}
            onResizeStart={onResizeStart}
            onResizeEnd={onResizeEnd}
            // Chrome rationale (overflow / handle pills) — see floatingChatShell.ts.
            className={
              isFullHeight ? DOCKED_PANEL_CLASS : FLOATING_WINDOW_PANEL_CLASS
            }
          >
            {renderPane({ isChatExpanded: isFullHeight })}
          </ResizablePanel>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Closed: the FAB launcher. While a full-width detail pane occupies the
  // right, it pins to the bottom-LEFT of the content region instead of the
  // region's bottom-right.
  return (
    <>
      {renderFab(
        dockLeft && anchorRect
          ? {
              left: anchorRect.left + 16,
              bottom: viewportHeightPx - anchorRect.bottom + 16,
            }
          : undefined,
      )}
    </>
  );
}
